import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { BrokerProvider } from './context/BrokerContext';
import { BrokerDataProvider } from './context/BrokerDataContext';

// User Portal Pages - New Flow
import CNICEntry from './pages/user/CNICEntry';
import AccountDashboard from './pages/user/AccountDashboard';
import BankSelection from './pages/user/BankSelection';
import IBANVerification from './pages/user/IBANVerification';
import OTPVerification from './pages/user/OTPVerification';
import PaymentDetails from './pages/user/PaymentDetails';
import ReferenceNumber from './pages/user/ReferenceNumber';
import UploadReceipt from './pages/user/UploadReceipt';
import TransactionStatus from './pages/user/TransactionStatus';

// Public Pages
import BrokerAPIRequirements from './pages/BrokerAPIRequirements';

// Admin Portal Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import TransactionReview from './pages/admin/TransactionReview';
import AccountVerification from './pages/admin/AccountVerification';
import UserManagement from './pages/admin/UserManagement';
import Reports from './pages/admin/Reports';
import AISettings from './pages/admin/AISettings';

// Layouts
import UserLayout from './components/layouts/UserLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Security - Prevents browser back navigation
import SecurityWrapper from './components/SecurityWrapper';

// Wrapper component that provides broker-specific data to admin pages
function AdminLayoutWithBrokerData() {
  return (
    <BrokerDataProvider>
      <AdminLayout />
    </BrokerDataProvider>
  );
}

function App() {
  return (
    <AppProvider>
      <Router>
        <BrokerProvider>
          <SecurityWrapper>
            <Routes>
              {/* Default route redirects to Savexa portal */}
              <Route path="/" element={<Navigate to="/savexa" replace />} />

              {/* Savexa Portal Routes */}
              <Route path="/savexa" element={<UserLayout />}>
                <Route index element={<CNICEntry />} />
                <Route path="account-dashboard" element={<AccountDashboard />} />
                <Route path="bank-selection" element={<BankSelection />} />
                <Route path="iban-verification" element={<IBANVerification />} />
                <Route path="otp-verification" element={<OTPVerification />} />
                <Route path="payment-details" element={<PaymentDetails />} />
                <Route path="reference-number" element={<ReferenceNumber />} />
                <Route path="receipt-upload" element={<UploadReceipt />} />
                <Route path="upload-receipt" element={<Navigate to="/savexa/receipt-upload" replace />} />
                <Route path="status/:transactionId" element={<TransactionStatus />} />
              </Route>

              {/* JustMarkets Portal Routes */}
              <Route path="/justmarkets" element={<UserLayout />}>
                <Route index element={<CNICEntry />} />
                <Route path="account-dashboard" element={<AccountDashboard />} />
                <Route path="bank-selection" element={<BankSelection />} />
                <Route path="iban-verification" element={<IBANVerification />} />
                <Route path="otp-verification" element={<OTPVerification />} />
                <Route path="payment-details" element={<PaymentDetails />} />
                <Route path="reference-number" element={<ReferenceNumber />} />
                <Route path="receipt-upload" element={<UploadReceipt />} />
                <Route path="upload-receipt" element={<Navigate to="/justmarkets/receipt-upload" replace />} />
                <Route path="status/:transactionId" element={<TransactionStatus />} />
              </Route>

              {/* Public Routes */}
              <Route path="/broker-api" element={<BrokerAPIRequirements />} />

              {/* Savexa Admin Portal Routes */}
              <Route path="/savexa/admin/login" element={<AdminLogin />} />
              <Route path="/savexa/admin" element={<AdminLayoutWithBrokerData />}>
                <Route index element={<Navigate to="/savexa/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="transactions" element={<TransactionReview />} />
                <Route path="account-verification" element={<AccountVerification />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="reports" element={<Reports />} />
                <Route path="ai-settings" element={<AISettings />} />
              </Route>

              {/* JustMarkets Admin Portal Routes */}
              <Route path="/justmarkets/admin/login" element={<AdminLogin />} />
              <Route path="/justmarkets/admin" element={<AdminLayoutWithBrokerData />}>
                <Route index element={<Navigate to="/justmarkets/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="transactions" element={<TransactionReview />} />
                <Route path="account-verification" element={<AccountVerification />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="reports" element={<Reports />} />
                <Route path="ai-settings" element={<AISettings />} />
              </Route>

              {/* Legacy admin route redirect to Savexa admin */}
              <Route path="/admin/login" element={<Navigate to="/savexa/admin/login" replace />} />
              <Route path="/admin/*" element={<Navigate to="/savexa/admin/dashboard" replace />} />
            </Routes>
          </SecurityWrapper>
        </BrokerProvider>
      </Router>
    </AppProvider>
  );
}

export default App;
